import { SearchIcon } from '@diligentcorp/atlas-react-icons';
import { useTranslation } from 'react-i18next';
import type { AutocompleteFreeSoloValueMapping } from '@mui/material';
import type { ExtendedAutocompleteProps, DefaultProps } from '../types';

export type UseDefaultSelectorReturn<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
> = DefaultProps<
  ExtendedAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
  'getOptionLabel'
>;

export function useExtendedAutocompleteDefaultProps<
  T,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
>(
  props: ExtendedAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
): UseDefaultSelectorReturn<T, Multiple, DisableClearable, FreeSolo> {
  const { t } = useTranslation('common');

  const {
    getOptionLabel: getOptionLabelProp,
    TextFieldProps,
    CancelButtonProps,
    ClearButtonProps,
    ApplyButtonProps,
  } = props;

  const getOptionLabel = getOptionLabelProp ?? defaultGetOptionLabel;

  return {
    ...props,
    noOptionsText: t('inputs.listBox.noResults'),
    getOptionLabel,
    TextFieldProps: {
      placeholder: t('inputs.listBox.search'),
      ...TextFieldProps,
      InputProps: {
        startAdornment: <SearchIcon />,
        ...TextFieldProps?.InputProps,
      },
    },
    ClearButtonProps: {
      children: t('inputs.listBox.clear'),
      ...ClearButtonProps,
    },
    ApplyButtonProps: {
      children: t('inputs.listBox.apply'),
      ...ApplyButtonProps,
    },
    CancelButtonProps: {
      children: t('inputs.listBox.cancel'),
      ...CancelButtonProps,
    },
  };
}

function defaultGetOptionLabel<T, FreeSolo>(
  option: T | AutocompleteFreeSoloValueMapping<FreeSolo>,
) {
  // TODO: Check whether we can fix this or determine if it's an issue with MUI types
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return typeof option === 'string' ? option : option.label;
}
